/**
 * GiftCardsTableRow — one row of the gift-cards table (BON-223).
 *
 * The operator wants the whole row to open the card, but a `<tr onClick>`
 * is neither focusable nor announced, and a SECOND decorative link doubles
 * the row's tab stops and confuses a screen reader about what it is. So the
 * Card cell keeps its one real, visibly-underlined `<Link>` and `giftCards.css`
 * stretches its hit area over the whole row via `::after` — a pseudo-element
 * is never in the accessibility tree, so this stays exactly one link.
 */
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { formatMoney } from '../review/reviewQueueModel';

import './giftCards.css';
import { formatCreatedDate, cardNumberLabel, sourceLabel } from './giftCardsModel';
import { GiftCardsTableCustomerCell } from './GiftCardsTableCustomerCell';
import { GiftCardStatusPill } from './GiftCardStatusPill';
import type { GiftCardListRow } from './giftCardTypes';

export function GiftCardsTableRow({ row }: { row: GiftCardListRow }) {
  return (
    // The positioned ancestor `.gc-stretched-link::after` stretches against —
    // it MUST be this `<tr>`, or the pseudo-element covers whatever positioned
    // element happens to sit above it instead (silently the wrong area).
    <tr style={{ position: 'relative' }}>
      <td style={{ paddingLeft: 18, whiteSpace: 'nowrap' }}>
        <Link
          to={`/admin/gift-cards/${row.id}`}
          className="nb-mono gc-stretched-link"
          aria-label={`View gift card ${cardNumberLabel(row)}`}
          style={{
            fontSize: 12.5,
            letterSpacing: '.04em',
            color: 'inherit',
            textDecoration: 'underline',
            textDecorationColor: 'var(--nb-border-2)',
            textUnderlineOffset: 2,
          }}
        >
          {cardNumberLabel(row)}
        </Link>
      </td>
      <td>
        <GiftCardsTableCustomerCell customer={row.customer} />
      </td>
      <td
        className="nb-tabular"
        style={{
          textAlign: 'right',
          fontWeight: 700,
          fontSize: 13.5,
          color: row.balanceCents === 0 ? 'var(--nb-faint)' : 'var(--nb-ink)',
        }}
      >
        {formatMoney(row.balanceCents, row.currency)}
      </td>
      <td>
        <GiftCardStatusPill status={row.status} />
      </td>
      <td
        style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--nb-muted)', whiteSpace: 'nowrap' }}
      >
        {sourceLabel(row.source)}
      </td>
      <td
        style={{
          fontSize: 12.5,
          color: 'var(--nb-muted)',
          whiteSpace: 'nowrap',
          paddingRight: 18,
        }}
      >
        {/* Non-interactive: the row's real link is stretched over the whole
            row now, so this chevron is purely a visual "this opens" cue. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          {formatCreatedDate(row.createdAt)}
          <ChevronRight
            size={14}
            aria-hidden
            style={{ color: 'var(--nb-faint)', flex: '0 0 auto' }}
          />
        </div>
      </td>
    </tr>
  );
}
